<template>
  <v-container fluid class="py-8">
    <!-- Header -->
    <v-row class="mb-4" justify="space-between">
      <v-col cols="auto">
        <span class="text-overline text-medium-emphasis d-block mb-1">Current Listings</span>
        <h1 class="text-h5 font-weight-bold">View and Manage Listings</h1>
      </v-col>
      <v-col cols="auto">
        <!-- no add buttons per your request -->
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card elevation="2" class="mb-6">
      <v-card-text>
        <v-row class="ga-2">
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.guestName" label="Guest name" variant="outlined" density="comfortable"
              clearable @keyup.enter="applyFilters" />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.guestPhone" label="Guest phone number" variant="outlined"
              density="comfortable" clearable @keyup.enter="applyFilters" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="filters.guestEmail" label="Guest email" variant="outlined" density="comfortable"
              clearable @keyup.enter="applyFilters" />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.bookingId" label="Booking ID" variant="outlined" density="comfortable"
              clearable @keyup.enter="applyFilters" />
          </v-col>

          <v-col cols="12" md="2" class="d-flex flex-column flex-md-row ga-4">
            <v-btn size="large" height="50" variant="outlined" color="grey" class="flex-grow-1" @click="clearFilters"
              :disabled="!hasFilters">
              Clear
            </v-btn>

            <v-btn size="large" height="46" color="primary" class="flex-grow-1" @click="applyFilters">
              Search
            </v-btn>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading skeleton -->
    <v-card v-if="isLoading" elevation="2" class="mb-6">
      <v-card-title>Listings</v-card-title>
      <v-card-text>
        <!-- header row -->
        <div class="d-flex mb-2">
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="16%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="12%" class="mr-4" />
          <v-skeleton-loader type="text" width="8%" class="ml-auto" />
        </div>
        <!-- rows -->
        <div v-for="n in pageSize" :key="n" class="d-flex align-center py-3">
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="16%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="12%" class="mr-4" />
          <v-skeleton-loader type="button" width="100" class="ml-auto" />
        </div>

        <!-- pagination skeleton -->
        <div class="d-flex justify-end mt-4">
          <v-skeleton-loader type="chip" class="mr-2" />
          <v-skeleton-loader type="chip" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Real table -->
    <v-card v-else elevation="2" class="mb-6">
      <v-card-title>Listings</v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th class="text-left">Cruise Line</th>
              <th class="text-left">Ship</th>
              <th class="text-left">Itinerary Name</th>
              <th class="text-left">Sail Date</th>
              <th class="text-left">Total Cost</th>
              <th class="text-left">Service Fee</th>
              <th class="text-left">Payout Amount</th>
              <th class="text-left">Created Date</th>
              <th class="text-left">Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="listing in listings" :key="listing.id">
              <td>{{ listing.itinerary?.ship?.cruiseLine || '—' }}</td>
              <td>{{ listing.itinerary?.ship?.name || '—' }}</td>
              <td>{{ listing.itinerary?.title || '—' }}</td>
              <td>{{ formatDate(listing.itinerary?.date) }}</td>
              <td>{{ formatCurrency(listing.total_cost) }}</td>
              <td>{{ formatCurrency(listing.service_fee) }}</td>
              <td>{{ formatCurrency(payoutAmount(listing.total_cost, listing.service_fee)) }}</td>
              <td>{{ formatDate(listing.created_at) }}</td>
              <!-- Current Step pill -->
              <td>
                <v-chip v-bind="chipAttrsFor(listing)" size="small" class="font-weight-medium w-100 text-center"
                  variant="flat">
                  <v-icon :icon="stepFor(listing).icon" start class="mr-2" />
                  {{ stepFor(listing).label }}
                </v-chip>
              </td>
              <td class="text-right">
                <v-btn v-if="isAgent" size="small" variant="outlined" color="secondary"
                  :to="`/dashboard/listings/${listing.id}`">
                  View
                </v-btn>
                <v-btn v-else size="small" variant="outlined" color="secondary"
                  :to="`/dashboard/listings/admin/${listing.id}`">
                  View
                </v-btn>
              </td>
            </tr>
            <tr v-if="(listings || []).length === 0">
              <td colspan="10" class="text-center text-medium-emphasis py-6">
                No listings found.
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" justify="space-between">
          <v-col cols="auto">
            <small class="text-medium-emphasis">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
            </small>
          </v-col>
          <v-col cols="auto">
            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="5" prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right" @update:modelValue="goToPage" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'

/* ---------- store / user ---------- */
const store = useStore()
const userData = computed(() => store.state.user || null)
const isAgent = computed(() => !!userData.value?.is_agent)

/* ---------- state ---------- */
const listings = ref([])
const pageSize = ref(10)
const currentPage = ref(1)
const totalPages = ref(1)
const totalCount = ref(0)
const isLoading = ref(true)
const mineOnly = ref(false) // agents see only their own

/* filters */
const filters = ref({
  guestName: '',
  guestPhone: '',
  guestEmail: '',
  bookingId: '',
})

/* steps (pill) */
const steps = ref([
  { key: 'pending_posting', label: 'Pending Approval', color: 'yellow', icon: 'mdi-clock-time-nine-outline' },
  { key: 'listing_posted', label: 'Listing Posted', color: 'blue', icon: 'mdi-bullhorn' },
  { key: 'booking_requested', label: 'Booking Requested', color: 'amber', icon: 'mdi-account-clock' },
  { key: 'pending_confirmation', label: 'Pending Confirmation', color: 'indigo', icon: 'mdi-help-circle-outline' },
  { key: 'pending_payment', label: 'Pending Payment', color: 'deep-orange', icon: 'mdi-cash-clock' },
  { key: 'booking_confirmed', label: 'Booking Confirmed', color: 'green', icon: 'mdi-check-circle' },
  { key: 'final_payment', label: 'Final Payment', color: 'teal', icon: 'mdi-cash-check' },
  { key: 'set_sail', label: 'Set Sail', color: 'cyan', icon: 'mdi-ship-wheel' },
  { key: 'end_summary', label: 'End Summary', color: 'grey', icon: 'mdi-file-document-check' },
])

const stepMap = computed(() => {
  const m = new Map()
  steps.value.forEach((s, i) => m.set(s.key, { ...s, index: i }))
  return m
})

/* ---------- computed ---------- */
const pageStart = computed(() => (currentPage.value - 1) * pageSize.value)
const pageEnd = computed(() => Math.min(pageStart.value + pageSize.value, totalCount.value || 0))

const hasFilters = computed(() => {
  const f = filters.value
  return !!(f.guestName || f.guestPhone || f.guestEmail || f.bookingId)
})
const hasBookingFilters = hasFilters // alias for clarity

/* ---------- formatters ---------- */
function formatDate(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt) ? '—' : dt.toLocaleDateString()
}
function formatCurrency(v) {
  if (v == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
}
function payoutAmount(totalCost, serviceFee) {
  const a = Number(totalCost) || 0
  const b = Number(serviceFee) || 0
  return a - b
}

/* ---------- step helpers ---------- */
function stepFor(listing) {
  const s = listing?.current_step
  if (typeof s === 'number' && Number.isFinite(s)) {
    const idx = Math.min(Math.max(0, s), steps.value.length - 1)
    return steps.value[idx]
  }
  if (typeof s === 'string') {
    return stepMap.value.get(s) ?? steps.value[0]
  }
  // fallback for legacy `status`
  if (typeof listing?.status === 'string') {
    return stepMap.value.get(listing.status) ?? steps.value[0]
  }
  return steps.value[0]
}
function chipAttrsFor(listing) {
  const s = stepFor(listing)
  return { color: s.color }
}

/* ---------- bookings filter -> listing ids ---------- */
async function getMatchingListingIds() {
  const f = filters.value
  let q = supabase.from('bookings')
    .select('listing_id')
    .order('created_at', { ascending: false })

  if (f.guestPhone?.trim()) q = q.ilike('phone', `%${f.guestPhone.trim()}%`)
  if (f.guestEmail?.trim()) q = q.ilike('email', `%${f.guestEmail.trim()}%`)
  if (f.bookingId?.trim()) q = q.ilike('booking_id', `%${f.bookingId.trim()}%`)
  if (f.guestName?.trim()) q = q.ilike('guests_text', `%${f.guestName.trim()}%`)

  const { data, error } = await q.limit(10000)
  if (error) throw error

  return Array.from(new Set((data || []).map(r => r.listing_id).filter(Boolean)))
}

/* ---------- main fetch ---------- */
async function fetchListings(page = 1, mineOnlyArg = mineOnly.value) {
  isLoading.value = true
  const from = (page - 1) * pageSize.value
  const to = from + pageSize.value - 1

  try {
    const uid = userData.value?.uid
    let listingIds = null

    if (hasBookingFilters.value) {
      listingIds = await getMatchingListingIds()
      if (!listingIds || listingIds.length === 0) {
        listings.value = []
        totalCount.value = 0
        totalPages.value = 1
        return
      }
    }

    let query = supabase.from('listings').select('*', { count: 'exact' })
    if (listingIds) query = query.in('id', listingIds)
    if (mineOnlyArg) {
      if (!uid) throw new Error('No current user uid available')
      query = query.eq('uid', uid)
    }

    const { data, count, error } = await query
      .order('created_at', { ascending: false })
      .range(from, to)

    if (error) throw error

    listings.value = data || []
    totalCount.value = count ?? listings.value.length
    totalPages.value = Math.max(1, Math.ceil((totalCount.value || 0) / pageSize.value))
  } catch (err) {
    console.error('Error fetching listings:', err)
    listings.value = []
    totalCount.value = 0
    totalPages.value = 1
  } finally {
    isLoading.value = false
  }
}

/* ---------- actions ---------- */
function goToPage(page) {
  if (page === currentPage.value) return
  currentPage.value = page
  fetchListings(page, mineOnly.value)
}
function applyFilters() {
  currentPage.value = 1
  fetchListings(1, mineOnly.value)
}
function clearFilters() {
  filters.value = { guestName: '', guestPhone: '', guestEmail: '', bookingId: '' }
  applyFilters()
}

/* ---------- mount ---------- */
onMounted(async () => {
  // Agents: only their listings; admins: all listings
  mineOnly.value = isAgent.value
  await fetchListings(currentPage.value, mineOnly.value)
})

</script>


<style scoped>
/* subtle polish for table look */
.v-table thead th {
  font-weight: 600;
}
</style>
